import React from 'react';
import { Button, TextButton, Tooltip } from '@ds';
import { DSArrowLeftIcon } from '@ds-icons';

import { useHistory } from 'react-router-dom';
import styles from './BasePythonPanelFooter.module.scss';
import {
  GALLERY_TAB,
  SELECT_PANEL_PATH
} from '@experiment-management-shared/constants/visualizationConstants';
import { noop } from 'lodash';
import useProject from '@API/project/useProject';
import { useSelector } from 'react-redux';
import { getSavedGalleryTab } from '@/reducers/ui/visualizationsUiReducer';
import { TEMPLATE_SCOPE_TO_PARAMETER_KEY_MAP } from '@/experiment-management-shared/constants/chartsGallery';

interface FooterActions {
  onAdd: () => void;
  onReset: () => void;
  onDone: () => void;
}

interface BasePythonPanelFooterProps {
  actions: FooterActions;
  settings?: {
    hideGoBackButton?: boolean;
    isPreview?: boolean;
    hasCodeBeenUpdated?: boolean;
  };
  isDoneBtnLoading?: boolean;
  renderCustomActions?: () => React.ReactNode;
  isDoneDisabled?: boolean;
}

const BasePythonPanelFooter = ({
  actions,
  isDoneBtnLoading,
  settings = {},
  renderCustomActions = noop,
  isDoneDisabled
}: BasePythonPanelFooterProps) => {
  const { onReset, onDone, onAdd } = actions;
  const { hideGoBackButton, isPreview = false, hasCodeBeenUpdated } = settings;
  const history = useHistory();

  const { data: project } = useProject();

  const hasWriteAccess = project?.canEdit;
  const savedGalleryTab = useSelector(getSavedGalleryTab);

  const onCancel = () => {
    if (hasCodeBeenUpdated) {
      const userConfirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );

      if (!userConfirmed) {
        return;
      }
    }

    history.push({
      hash: `${SELECT_PANEL_PATH}?${GALLERY_TAB}=${TEMPLATE_SCOPE_TO_PARAMETER_KEY_MAP[savedGalleryTab]}`
    });
  };

  const renderBackButton = () =>
    !hideGoBackButton ? (
      <TextButton
        onClick={onCancel}
        PrefixIcon={<DSArrowLeftIcon />}
        size="large"
      >
        Back
      </TextButton>
    ) : (
      <div />
    );

  const renderContent = () => {
    if (isPreview) {
      return (
        <div className={styles.footerActionsContainer}>
          <Button size="large" type="tertiary" onClick={onAdd}>
            Add to dashboard
          </Button>
        </div>
      );
    }

    const tooltipContent = !hasWriteAccess && (
      <p className={styles.tooltipWrapper}>
        {`You don't have proper permission to edit a Python panel.`}
      </p>
    );

    return (
      <div className={styles.footerActionsContainer}>
        <TextButton size="large" onClick={onReset}>
          Reset
        </TextButton>
        {hasWriteAccess && renderCustomActions()}
        <Tooltip content={tooltipContent}>
          <Button
            disabled={!hasWriteAccess || isDoneDisabled}
            loading={isDoneBtnLoading}
            size="large"
            onClick={onDone}
          >
            Done
          </Button>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="gallery-modal-footer">
      {renderBackButton()}
      {renderContent()}
    </div>
  );
};

export default BasePythonPanelFooter;
